/**
 * @file HeaderFilterMenu.tsx
 * @brief A page-header filter: a 56px round pill with its value as text,
 *        opening a searchable dropdown menu under itself.
 *
 * @details
 * DES (01-10-2026), on Sessions: "inko bhi yehi karo, bus text saath show
 * karna". The status / kind / employee selects move up beside the calendar
 * circle (`DateRangePicker variant="circle"`) and share its look: the same
 * height, grey glass and red fill once a value is set, with a corner ✕ that
 * clears it. Unlike the circle they show their value as text. The list is
 * `SearchableMenuList`, our one picker kit.
 *
 * @author DES
 * @date 01-10-2026
 * @updated 01-10-2026: optional server-side search (`query` /
 *          `onQueryChange`) and `onOpenChange`, for the user/company pills
 */

import { useState, type ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { cn } from "@/shared/theme/cn";

export interface HeaderFilterMenuProps {
  /** Text on the pill: the chosen value, or the "any" wording. */
  label: string;
  /** A value is set: red fill and the corner ✕. */
  active: boolean;
  onClear: () => void;
  /** Leading icon or avatar on the pill. */
  icon?: ReactNode;
  title: string;
  searchPlaceholder?: string;
  items: SearchableMenuItem[];
  selected: string;
  onSelect: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  align?: "start" | "center" | "end";
  className?: string;
  /** Server-side search: the caller filters `items` (see SearchableMenuList). */
  query?: string;
  onQueryChange?: (q: string) => void;
  /** Told when the menu opens or closes (e.g. to fetch options lazily). */
  onOpenChange?: (open: boolean) => void;
  emptyText?: ReactNode;
  noMatchText?: ReactNode;
  /** 68px, to sit beside the Boardrooms page's Ask pill and circles. */
  tall?: boolean;
}

export function HeaderFilterMenu({
  label,
  active,
  onClear,
  icon,
  title,
  searchPlaceholder,
  items,
  selected,
  onSelect,
  loading,
  disabled,
  align = "end",
  className,
  query,
  onQueryChange,
  onOpenChange,
  emptyText,
  noMatchText,
  tall = false,
}: HeaderFilterMenuProps) {
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  return (
    <span className={cn("relative inline-flex min-w-0 shrink-0", className)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            title={label}
            className={cn(
              "flex h-14 min-w-0 max-w-[240px] items-center gap-2 rounded-full border pl-5 pr-4 text-[15px] font-medium transition-colors disabled:opacity-50",
              tall && "h-[68px] max-w-[280px] text-[17px]",
              icon && "pl-2.5",
              active
                ? "border-brand bg-brand text-white hover:bg-brand/90"
                : "border-black/5 bg-black/[0.04] text-foreground hover:bg-black/[0.08] dark:border-white/[0.07] dark:bg-white/[0.06]",
            )}
          >
            {icon ? <span className="flex shrink-0 items-center">{icon}</span> : null}
            <span className="truncate">{label}</span>
            <ChevronDown
              className={cn("h-4 w-4 shrink-0", active ? "text-white/80" : "text-muted-foreground")}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-72 p-1.5">
          <SearchableMenuList
            title={title}
            searchPlaceholder={searchPlaceholder}
            items={items}
            loading={loading}
            query={query}
            onQueryChange={onQueryChange}
            emptyText={emptyText}
            noMatchText={noMatchText}
            selected={[selected]}
            onSelect={(v) => {
              onSelect(v);
              setOpen(false);
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
      {active ? (
        <button
          type="button"
          onClick={onClear}
          aria-label={`Clear ${title.toLowerCase()}`}
          className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-app-page bg-app-text text-app-page"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      ) : null}
    </span>
  );
}
